/**
 * The only place in the project that turns a `Pose` into transforms.
 *
 * Two responsibilities, and they are both about safety rather than cleverness:
 *
 * 1. **Bind capture.** Her ears, snout and tail have non-zero rotations baked in
 *    by the mesh factory. Poses are deltas (see `types.ts`), so the bind
 *    transforms are read once at construction and every applied value is added
 *    to them. Without this, the first frame of any clip snaps her ears flat.
 *
 * 2. **Clamping D-002.** Squash and stretch is capped at 5 % here, not in the
 *    clips. A limit that lives in the caller is a limit that gets exceeded by
 *    the next caller, and "just a bit more on this one landing" is precisely how
 *    a restrained plush becomes a cartoon.
 */

import * as THREE from 'three';
import { findRosalieParts, type RosalieParts } from '@/models/rosalie';
import type { LegPose, Pose } from './types';

/** D-002: "Squash & stretch: 3–5 %." This is the hard edge of that range. */
const SQUASH_LIMIT = 0.05;

/**
 * How far the tuck may compress her along her length. See the D-002 amendment.
 *
 * A SEPARATE limit from `SQUASH_LIMIT` on purpose. The 5 % squash cap exists to
 * stop comic squash creeping into landings one clip at a time, and it is not
 * widened here — the tuck is a distinct, named channel with its own ceiling, so
 * anything reaching for extra compression on an impact still finds 5 % and
 * nothing else.
 *
 * 22 %: enough that a 0.55 m body reads as curling into a ball rather than
 * rolling like a log, and short of the point where she stops being recognisably
 * herself mid-roll.
 */
const TUCK_LIMIT = 0.22;

/**
 * Hard ceiling on the COMPOSED deviation from 1 on any axis.
 *
 * An independent review caught the comment here overclaiming. The old text said
 * the 5 % squash cap was "unreachable from the tuck path"; it is not, and
 * cannot be, because the two multiply — a 5 % squash under a 17 % tuck composes
 * to ~13 % on X, whichever nodes they sit on. Separating the transforms would
 * not have changed that either: nested scales compose to the same product.
 *
 * So here is the invariant that IS true and IS worth having, stated plainly:
 *
 *   - `squash` alone can never exceed 5 %. Unchanged, and that is the D-002 cap
 *     that stops comic squash creeping into landings.
 *   - `tuck` is writable by `rollClip` and by nothing else, so no landing,
 *     stop or turn can reach the extra range at all. That is grep-able.
 *   - Their composition is bounded HERE, so even a clip doing both at once
 *     cannot deform her arbitrarily.
 */
const TOTAL_SCALE_LIMIT = 0.28;

function clampTotal(v: number): number {
  return Math.min(1 + TOTAL_SCALE_LIMIT, Math.max(1 - TOTAL_SCALE_LIMIT, v));
}

interface BoundLeg {
  node: THREE.Object3D;
  bindY: number;
  bindRotX: number;
  bindRotZ: number;
}

function clampSquash(v: number): number {
  return Math.min(1 + SQUASH_LIMIT, Math.max(1 - SQUASH_LIMIT, v));
}

export class RosalieRig {
  readonly parts: RosalieParts;

  #squashBindY: number;
  #headBind: THREE.Euler;
  #snoutBind: THREE.Euler;
  #earLBind: THREE.Euler;
  #earRBind: THREE.Euler;
  #tailBind: THREE.Euler;
  #legs: { FL: BoundLeg; FR: BoundLeg; BL: BoundLeg; BR: BoundLeg };

  constructor(root: THREE.Object3D) {
    this.parts = findRosalieParts(root);
    const p = this.parts;

    this.#squashBindY = p.squash.position.y;
    this.#headBind = p.head.rotation.clone();
    this.#snoutBind = p.snout.rotation.clone();
    this.#earLBind = p.earL.rotation.clone();
    this.#earRBind = p.earR.rotation.clone();
    this.#tailBind = p.tail.rotation.clone();

    const bind = (node: THREE.Object3D): BoundLeg => ({
      node,
      bindY: node.position.y,
      bindRotX: node.rotation.x,
      bindRotZ: node.rotation.z,
    });
    this.#legs = {
      FL: bind(p.legFL),
      FR: bind(p.legFR),
      BL: bind(p.legBL),
      BR: bind(p.legBR),
    };
  }

  /** Write a pose onto the rig. Allocates nothing. */
  apply(pose: Pose): void {
    const p = this.parts;

    p.squash.position.y = this.#squashBindY + pose.bob;
    // The tuck shortens her along Z and lets that volume out sideways and up,
    // so she balls up rather than merely getting smaller. Clamped here, not
    // trusted from the clip — same reasoning as the squash cap above.
    const tuck = Math.min(TUCK_LIMIT, Math.max(0, pose.tuck * TUCK_LIMIT));
    p.squash.scale.set(
      clampTotal(clampSquash(pose.squashX) * (1 + tuck * 0.45)),
      clampTotal(clampSquash(pose.squashY) * (1 + tuck * 0.35)),
      clampTotal(clampSquash(pose.squashZ) * (1 - tuck)),
    );
    p.squash.rotation.set(pose.pitch, pose.yaw, pose.roll);

    this.#applyLeg(this.#legs.FL, pose.legFL, 1);
    this.#applyLeg(this.#legs.FR, pose.legFR, -1);
    this.#applyLeg(this.#legs.BL, pose.legBL, 1);
    this.#applyLeg(this.#legs.BR, pose.legBR, -1);

    p.head.rotation.set(
      this.#headBind.x + pose.headPitch,
      this.#headBind.y + pose.headYaw,
      this.#headBind.z + pose.headRoll,
    );
    p.snout.rotation.set(
      this.#snoutBind.x + pose.snoutPitch,
      this.#snoutBind.y + pose.snoutYaw,
      this.#snoutBind.z,
    );
    p.earL.rotation.set(
      this.#earLBind.x + pose.earLFlick,
      this.#earLBind.y,
      this.#earLBind.z + pose.earLSplay,
    );
    p.earR.rotation.set(
      this.#earRBind.x + pose.earRFlick,
      this.#earRBind.y,
      // Mirrored: her right ear's bind splay is negative, so a positive splay in
      // the pose must push it the other way or "ears out" bends them both left.
      this.#earRBind.z - pose.earRSplay,
    );
    p.tail.rotation.set(
      this.#tailBind.x + pose.tailPitch,
      this.#tailBind.y + pose.tailYaw,
      this.#tailBind.z,
    );
  }

  #applyLeg(leg: BoundLeg, pose: LegPose, side: 1 | -1): void {
    leg.node.position.y = leg.bindY + pose.lift;
    leg.node.rotation.x = leg.bindRotX + pose.swing;
    leg.node.rotation.z = leg.bindRotZ + pose.splay * side;
  }
}
